// s119 (D532) · iOS NATIVE PUSH. His ruling 2026-10-24: "we need iOS push for all the app functions".
// The iOS app is a WKWebView with no PushManager, so the browser push (services/push.js) can never reach an
// iPhone. Here the app asks iOS for permission once per signed-in session, registers with APNs, and hands the
// device token to the API (POST /push/apns-token). The API sends every notice with the same fixed title and body
// as the browser push (no names, dates or clinical words). Tapping a push opens the app at the notice's deep link
// (relative paths only). An ACTIVE sign-out removes this phone's token first (the token is still valid then);
// the automatic idle sign-out keeps it, exactly like the browser push (PROD-1181).
// Web and Android: every export is a no-op. Gate: scripts/check-native-push.mjs.
import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { pushAPI } from './api'
import { safePushPath } from '../utils/pushPath.js'

let listening = false
let currentToken = null
let navigateTo = null

export function isNativePush() {
  try { return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios' } catch (e) { return false }
}

async function listen() {
  if (listening) return
  listening = true
  await PushNotifications.addListener('registration', (t) => {
    const value = t && typeof t.value === 'string' ? t.value : ''
    if (!value) return
    currentToken = value
    pushAPI.registerApnsToken(value).catch(() => { /* tried again at the next sign-in */ })
  })
  await PushNotifications.addListener('registrationError', () => { /* no token: the in-app list still has every notice */ })
  await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const path = safePushPath(action && action.notification && action.notification.data && action.notification.data.url)
    if (navigateTo) navigateTo(path)
  })
}

/**
 * Ask for permission (iOS shows its prompt once; later calls read the answer), register with APNs and send the
 * token to the API. Returns { ok, reason? } like ensurePushSubscribed, so the Settings pill can use either.
 */
export async function startNativePush(navigate) {
  if (!isNativePush()) return { ok: false, reason: 'unsupported' }
  if (typeof navigate === 'function') navigateTo = navigate
  try {
    await listen()
    let perm = await PushNotifications.checkPermissions()
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions()
    }
    if (perm.receive !== 'granted') return { ok: false, reason: 'denied' }
    await PushNotifications.register()
    return { ok: true }
  } catch (e) {
    return { ok: false, reason: 'failed' }
  }
}

/** ACTIVE sign-out only: this phone stops receiving this account's pushes. Bounded so sign-out never hangs. */
export async function stopNativePush(maxWaitMs = 1500) {
  if (!isNativePush() || !currentToken) return { ok: true, reason: 'nothing_to_do' }
  const token = currentToken
  let timer
  const limit = new Promise((resolve) => { timer = setTimeout(() => resolve({ ok: false, reason: 'timeout' }), maxWaitMs) })
  const work = pushAPI.removeApnsToken(token).then(() => { currentToken = null; return { ok: true } }, () => ({ ok: false, reason: 'failed' }))
  try { return await Promise.race([work, limit]) } finally { clearTimeout(timer) }
}
